import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import countryList from "react-select-country-list";
import {
  getCountry,
  getOccupation,
  getQuotePersonalAccident,
  getTypeOfTransaction,
  sendOTPRequest,
} from "../../Redux/PersonalAccidentRedux";
import "./PersonalInformationStyle.scss";
import { getQuoteTermPlan } from "../../Redux/TermPlanRedux";
import { formatDate, toDisplayDate } from "../Common/FormatData/FormatData";
import { useTranslation } from "react-i18next";
import { updateLeadsLife } from "../../Redux/LogsRedux/Life/LifeLeadsRedux";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import greenTick from "../../assets/images/greentick.svg";
import { getValidAccessToken } from "../../Services/oauthServices";
import OtpModal from "../OtpModal/OtpModal";
import { modalOpenMedical } from "../../Redux/LoadingRedux";
import TextInputSecondary from "../ui/input/TextInputSecondary";
import omanFlag from "../../assets/images/oman-flag.png";
// import { sponsorTypeOptions } from "../Common/FieldValues/DropDowns";

dayjs.extend(customParseFormat);

const DEFAULT_CIVIL_ID_EXPIRY_LIMIT_YEARS = 2;
const OMAN_CIVIL_ID_EXPIRY_LIMIT_YEARS = 10;

const getCivilIdExpiryLimitYears = (nationality) =>
  String(nationality || "").trim().toUpperCase() === "OMAN"
    ? OMAN_CIVIL_ID_EXPIRY_LIMIT_YEARS
    : DEFAULT_CIVIL_ID_EXPIRY_LIMIT_YEARS;

function PersonalInformation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useSelector((state) => state.loading);
  // const [country, setCountry] = useState([]);
  const [country, setCountry] = useState([]);

  const [occupation, setOccupation] = useState([]);
  const [errMessage, setErrMessage] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [openOtpModal, setOpenOtpModal] = useState(false);
  const [otpResult, setOtpResult] = useState(null);
  const [otpValue, setOtpValue] = useState("");
  const [formData, setFormData] = useState({});
  const [whatsappChecked, setWhatsappChecked] = useState(false);
  const [mobileChecked, setMobileChecked] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [sameAsMobile, setSameAsMobile] = useState(false);
  const [typeOfTransaction, setTypeOfTransaction] = useState();

  const location = useLocation();
  const { planDetails } = location.state || {};
  const DomainReducer = useSelector((state) => state?.DomainReducer || {});
  const partnerDetail = DomainReducer?.partnerDetail || {};
  const { partnerSmsConfig = {} } = useSelector(
    (state) => state.partnerSmsConfig || {},
  );
  const { IS_EMAIL_MANDATORY = 0, EMAIL_DOMAIN = "" } = partnerSmsConfig || {};

  useEffect(() => {
    dispatch(getCountry()).then((result) => {
      if (result && result.success) {
        setCountry(Array.isArray(result.data) ? result.data : []);
      }
    });

    dispatch(getOccupation()).then((result) => {
      if (result && result.success) {
        setOccupation(Array.isArray(result.data) ? result.data : []);
      }
    });
  }, [dispatch]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Full name is required").test('no-script', 'Script tags are not allowed', value => !/<script/i.test(value)),

    height: Yup.string()
      .required("Height is required")
      .max(3, "Height must be at most 3 digits"),

    weight: Yup.string()
      .required("Weight is required")
      .max(3, "Weight must be at most 3 digits"),

    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required").test('no-script', 'Script tags are not allowed', value => !/<script/i.test(value)),
    mobile: Yup.string()
      .matches(/^\d{8}$/, "Enter 8 digits")
      .required("Mobile number is required"),
    civilId: Yup.string()
      .matches(/^\d+$/, "Only digits are allowed")
      .min(4, "Must be at least 4 digits")
      .max(9, "Must be at most 9 digits")
      .required("Civil ID is required"),
    nationality: Yup.string().required("Nationality is required"),
    occupation: Yup.string().required("Occupation is required"),
    civilIdExpiry: Yup.string()
      .required("Civil ID expiry is required")
      .test(
        "civil-id-expiry-limit",
        ({ parent }) =>
          `Civil ID expiry cannot be more than ${getCivilIdExpiryLimitYears(parent?.nationality)} years from today`,
        function (value) {
          if (!value) return true;

          const selectedDate = dayjs(value, "DD-MM-YYYY", true);
          if (!selectedDate.isValid()) return false;

          const limitYears = getCivilIdExpiryLimitYears(this.parent?.nationality);
          const maxAllowedDate = dayjs()
            .add(limitYears, "year")
            .endOf("day");

          return (
            !selectedDate.isBefore(dayjs().startOf("day")) &&
            !selectedDate.isAfter(maxAllowedDate)
          );
        },
      ),
    passportNumber: Yup.string().required("Passport number is required"),
    passportExpiryDate:
      // planDetails?.coverPlan !== "Term plan"
      Yup.string().required("Passport expired is required"),
    location: Yup.string().required("Resident Location is required").test('no-script', 'Script tags are not allowed', value => !/<script/i.test(value)),
  });

  const countryOptions = useMemo(() => countryList().getData(), []);

  // Memoize occupation options to avoid recreating large arrays on every render
  const options = useMemo(
    () =>
      occupation.map((o) => ({
        value: o.CODE,
        label: selectedLanguage === "ar" ? o.ARABIC : o.ENGLISH,
      })),
    [occupation, selectedLanguage],
  );

  const transactionOptions = useMemo(() => {
    if (!Array.isArray(typeOfTransaction)) return [];

    return typeOfTransaction.map((item) => {
      if (typeof item === "string") {
        return { value: item, label: item };
      }

      const value =
        item?.value ||
        item?.VALUE ||
        item?.code ||
        item?.CODE ||
        item?.name ||
        item?.NAME ||
        item?.label ||
        item?.LABEL ||
        "";

      const label =
        item?.label ||
        item?.LABEL ||
        item?.name ||
        item?.NAME ||
        item?.value ||
        item?.VALUE ||
        value;

      return {
        value,
        label,
      };
    });
  }, [typeOfTransaction]);

  const shouldShowPreviousPolicyFields = (transactionType) => {
    const normalizedType = String(transactionType || "").trim().toUpperCase();
    return normalizedType !== "" && normalizedType !== "NEW";
  };

  const initialValues = {
    coverPlan: planDetails?.coverPlan || "",
    name: planDetails?.name || "",
    email: planDetails?.personalInformation?.personalInformation?.email || "",
    mobile: planDetails?.mobileNumber || "",
    civilId: planDetails?.personalInformation?.civil_id || "",
    passportNumber:
      planDetails?.personalInformation?.personalInformation?.passport || "",
    height: planDetails?.personalInformation?.personalInformation?.height || "",
    weight: planDetails?.personalInformation?.personalInformation?.weight || "",
    occupation:
      planDetails?.personalInformation?.personalInformation?.occupation ||
      "Domestic Servant",
    occupation_code:
      planDetails?.personalInformation?.personalInformation?.occupation_code ||
      "5132075",
    location:
      planDetails?.personalInformation?.personalInformation
        ?.resident_location || "",
    passportExpiryDate:
      toDisplayDate(
        planDetails?.personalInformation?.personalInformation
          ?.passport_expiry_date,
      ) || "",
    typeOfTransaction:
      planDetails?.personalInformation?.personalInformation
        ?.type_of_transaction || "NEW",
    previousPolicyNumber:
      planDetails?.personalInformation?.personalInformation
        ?.previous_policy_number || "",
    previousPolicyExpiryDate:
      toDisplayDate(
        planDetails?.personalInformation?.personalInformation
          ?.previous_policy_expiry_date,
      ) || "",
    previousCompanyName:
      planDetails?.personalInformation?.personalInformation
        ?.previous_company_name || "",
    civilIdExpiry:
      toDisplayDate(
        planDetails?.personalInformation?.personalInformation?.id_expiry_date,
      ) || "",
    nationality: "OMAN",
    vatin: planDetails?.personalInformation?.personalInformation?.vatin || "",
    sendViaWhatsapp: false,
    country: "",
    phone: "",
  };

  const handleSubmit = (values) => {
    const showPreviousPolicyFields = shouldShowPreviousPolicyFields(
      values.typeOfTransaction,
    );
    const selectedLimit = planDetails?.selectedLimit || {};
    const planNameRaw = selectedLimit?.planName || "";
    const planType = planNameRaw.split(" ")[0];

    const annual_limit =
      selectedLimit.annualLimit != null
        ? Number(selectedLimit.annualLimit).toLocaleString()
        : "";

    const op =
      selectedLimit.opDetectable != null
        ? String(selectedLimit.opDetectable)
        : "";

    const co_pay =
      selectedLimit.coPay != null && selectedLimit.coPay !== "Nil"
        ? (() => {
            const raw = String(selectedLimit.coPay).trim();
            return raw.includes("%") ? raw : `${raw}%`;
          })()
        : "0%";

    const civilIdExpiryDate = formatDate(values.civilIdExpiry) || "";

    const passportExpiryDate = formatDate(values.passportExpiryDate) || "";

    const infoDetails = {
      transId: localStorage.getItem("LIFE_LEAD_ID"),
      nationality: values.nationality || "",
      civilIdNo: values.civilId || "",
      sendDocumentViaWhatsapp: whatsappChecked ? 1 : 0,
      sendDocumentViaSms: mobileChecked ? 1 : 0,
      sendDocumentViaMail: emailChecked ? 1 : 0,
      sponsorName: values.sponsorName || "",
      sponsorId: values.sponsorId || "",
      sponsorType: values.sponsorType || "individual",
      fullName: values.name || "",
      emailId: values.email || "",
      mobileNo: values.mobile || "",
      dob: planDetails?.basicInfo?.dob || "",
      residentialLocation: values.location || "",
      passportNo: values.passportNumber || "",
      gender: planDetails?.basicInfo?.gender || "",
      height: values.height || "",
      weight: values.weight || "",
      occupation: values.occupation || "",
      vatin: values.vatin || "",
      whatsappNo: values.phone || "",
      exipiryDateCivilid: values.civilIdExpiry || "",
      expiryDatePassport: values.passportExpiryDate || "",
      typeOfTransaction: values.typeOfTransaction || "",
      previousPolicyNumber: showPreviousPolicyFields
        ? values.previousPolicyNumber || ""
        : "",
      previousPolicyExpiryDate: showPreviousPolicyFields
        ? values.previousPolicyExpiryDate || ""
        : "",
      previousCompanyName: showPreviousPolicyFields
        ? values.previousCompanyName || ""
        : "",
    };
    const payload = {
      nationality: values.nationality || "",
      civil_id: values.civilId || "",
      sum_assured:
        planDetails?.coverPlan === "Personal Accident"
          ? planDetails?.selectedLimit?.sumAssured || ""
          : planDetails?.coverPlan === "Term plan"
            ? planDetails?.selectedLimit?.sum_assured || ""
            : "",
      send_documents_via_whatsapp: values.sendViaWhatsapp || false,
      send_documents_via_sms: mobileChecked || false,
      send_documents_via_email: emailChecked || false,
      personalInformation: {
        fullName: values.name || "",
        email: values.email || "",
        mobileNumber: values.mobile || "",
        dob: planDetails?.basicInfo?.dob || "",
        resident_location: values.location || "",
        passport: values.passportNumber || "",
        gender: planDetails?.basicInfo?.gender || "",
        is_married: planDetails?.basicInfo?.married || "",
        height: values.height || "",
        weight: values.weight || "",
        occupation: values.occupation,
        occupation_code: values.occupation_code || "",
        vatin: values.vatin || "",
        country: values.country || "",
        phone: values.phone || "",
        id_expiry_date: values.civilIdExpiry,
        passport_expiry_date: values.passportExpiryDate,
        type_of_transaction: values.typeOfTransaction || "",
        previous_policy_number: showPreviousPolicyFields
          ? values.previousPolicyNumber || ""
          : "",
        previous_policy_expiry_date: showPreviousPolicyFields
          ? values.previousPolicyExpiryDate || ""
          : "",
        previous_company_name: showPreviousPolicyFields
          ? values.previousCompanyName || ""
          : "",
      },
      beneficiaries: planDetails?.beneficiary,
    };

    const updatedData = {
      ...planDetails,
      personalInformation: payload,
    };

    if (planDetails?.coverPlan === "Personal Accident") {
      const personalAccidentPayload = {
        cover_type: "Personal Accident",
        application_type: "self",
        plan: "Personal Accident",
      };
      const finalPayload = { ...payload, ...personalAccidentPayload };
      dispatch(getQuotePersonalAccident(finalPayload)).then((result) => {
        if (result.success) {
          const payload = { ...infoDetails, amount: result.data.amount };
          dispatch(updateLeadsLife(payload)).then((response) => {
            if (response.success) {
              const updatedData = {
                ...planDetails,
                quoteDetails: result.data,
                personalInformation: finalPayload,
              };
              navigate("/question/life-personal", {
                state: { planDetails: updatedData },
              });
            }
          });
        }
      });
    }
    if (planDetails?.coverPlan === "Term plan") {
      const termPlanPayload = {
        cover_type: "Term Plan",
        application_type: "self",
        plan: "Term Plan",
      };
      const finalPayload = { ...payload, ...termPlanPayload };
      dispatch(getQuoteTermPlan(finalPayload)).then((result) => {
        if (result.success) {
          const payload = { ...infoDetails, amount: result.data.amount };
          dispatch(updateLeadsLife(payload)).then((response) => {
            if (response.success) {
              const updatedData = {
                ...planDetails,
                quoteDetails: result.data,
                personalInformation: finalPayload,
              };
              navigate("/question/life-term", {
                state: { planDetails: updatedData },
              });
            }
          });
        }
      });
    }
  };

  const getPickerDate = (value) => {
    if (!value) return null;
    const displayDate = dayjs(value, "DD-MM-YYYY", true);
    if (displayDate.isValid()) return displayDate;
    const isoDate = dayjs(value);
    return isoDate.isValid() ? isoDate : null;
  };

  useEffect(() => {
    dispatch(getTypeOfTransaction()).then((result) => {
      if (result.success) {
        setTypeOfTransaction(result?.data);
      }
    });
  }, [dispatch]);

  const validateOtpInputs = (quoteInfo) => {
    const mobileValid = /^\d{8}$/.test(quoteInfo.mobile || "");
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(quoteInfo.email || "");
    const whatsappValid = Boolean(quoteInfo.phone);

    if (!mobileChecked) {
      return "Please provide a mobile number to receive OTP.";
    }

    if (mobileChecked && !mobileValid) {
      return "Please provide a valid mobile number.";
    }

    if (emailChecked && !emailValid) {
      return "Please provide a valid email address.";
    }

    if (whatsappChecked && !whatsappValid) {
      return "Please provide a WhatsApp number.";
    }

    return null;
  };
  console.log("otpVerified", otpVerified);

 const sendOtp = async (quoteInfo) => {
     try {
       dispatch({ type: "LOADING", payload: true });
 
       const token = await getValidAccessToken();
 
       setFormData(quoteInfo);
       const validationError = validateOtpInputs(quoteInfo);
 
       if (validationError) {
         dispatch(
           modalOpenMedical({
             open: true,
             message: "Required fields missing or invalid",
             errors: [validationError],
           }),
         );
 
         return;
       }
 
       const request = {
         channel: IS_EMAIL_MANDATORY === 1 ? "email" : "sms",
         destination:
           IS_EMAIL_MANDATORY === 1 ? quoteInfo.email : quoteInfo.mobile,
         partnerId: partnerDetail?.PARTNER_ID,
       };
 
       const result = await dispatch(sendOTPRequest(request));
       if (result?.success) {
         setOtpResult(result.data[0]);
         setOpenOtpModal(true);
       } else {
         const errorMessage =
           result?.message ||
           result?.data?.message ||
           result?.payload?.message ||
           "Unable to send OTP. Please try again.";
         setOpenOtpModal(false);
         dispatch(
           modalOpenMedical({
             open: true,
             message: "OTP Send Failed",
             errors: [errorMessage],
           }),
         );
       }
     } catch (error) {
       console.error("Error sending OTP:", error);
 
       dispatch(
         modalOpenMedical({
           open: true,
           message: "Unexpected Error",
           errors: ["Unexpected error occurred while sending OTP."],
         }),
       );
     } finally {
       dispatch({ type: "LOADING", payload: false });
     }
   };

  const handleVerified = async () => {
    setOtpVerified(true);
    setOpenOtpModal(false);
    dispatch({ type: "LOADING", payload: false });
  };

  const handleBack = () => {
    navigate("/basic-information", {
        state: { planDetails, showPlans: true },
      });
  };

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          setFieldValue,
          setFieldTouched,
          setErrors,
          errors,
          touched,
        }) => {
          console.log("values", values);

          const h = Number(values.height);
          const w = Number(values.weight);

          if (h > 0 && w > 0) {
            const bmi = (w / Math.pow(h / 100, 2)).toFixed(2);
            if (values.bmi !== bmi) setFieldValue("bmi", bmi);
          }

          const selectedOccupation =
            options.find(
              (opt) =>
                String(opt.value).trim() ===
                String(values.occupation_code).trim(),
            ) || null;
          const civilIdExpiryMaxDate = dayjs()
            .add(getCivilIdExpiryLimitYears(values.nationality), "year")
            .endOf("day");

          const handleemailcheck = (value) => {
            setEmailChecked(value);
          };

          const handleMobilecheck = (value) => {
            setMobileChecked(value);
          };

          return (
            <>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Form>
                  <div className="row">
                    <div className="col-md-12 p-3">
                      <h3 className="title pt-2 mb-2">
                        {t(planDetails?.coverPlan)}
                      </h3>
                      <h5 className="title subHeader p-0 fw-bold py-1 mb-2">
                        {t("Enter Personal Information")}
                      </h5>
                    </div>
                  </div>

                  <div className="personal-form-grid">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label className="form-label fw-semibold">
                            {t("Civil ID Number")}
                          </label>
                          <Field
                            name="civilId"
                            className={`form-control ${touched.civilId && errors.civilId ? "is-invalid" : ""}`}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, "");
                              if (val.length <= 9)
                                setFieldValue("civilId", val);
                            }}
                            placeholder={t("Civil ID Number")}
                          />
                          <ErrorMessage
                            name="civilId"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label className="form-label fw-semibold">
                            {t("Expiry Date of Civil ID")}
                          </label>
                          <DatePicker
                            value={getPickerDate(values.civilIdExpiry)}
                            onChange={(value) => {
                              setFieldValue(
                                "civilIdExpiry",
                                value && value.isValid?.()
                                  ? value.format("DD-MM-YYYY")
                                  : "",
                              );
                            }}
                            format="DD-MM-YYYY"
                            disablePast
                            maxDate={civilIdExpiryMaxDate}
                            slotProps={{
                              textField: {
                                name: "civilIdExpiry",
                                className: `form-control ${
                                  touched.civilIdExpiry && errors.civilIdExpiry
                                    ? "is-invalid"
                                    : ""
                                }`,
                                onBlur: () =>
                                  setFieldTouched("civilIdExpiry", true),
                                onKeyDown: (e) => {
                                  if (e.key !== "Tab") e.preventDefault();
                                },
                                sx: {
                                  "& input": {
                                    height: "26px",
                                    padding: "0.375rem 0.75rem",
                                  },
                                },
                              },
                            }}
                          />
                          <ErrorMessage
                            name="civilIdExpiry"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label className="form-label fw-semibold">
                            {t("Nationality")}
                          </label>
                          <Select
                            name="nationality"
                            isClearable
                            options={country.map((o) => ({
                              value: o,
                              label: o,
                            }))}
                            onChange={(opt) =>
                              setFieldValue("nationality", opt ? opt.value : "")
                            }
                            value={
                              values.nationality
                                ? {
                                    value: values.nationality,
                                    label: values.nationality,
                                  }
                                : null
                            }
                          />
                          {touched.nationality && errors.nationality && (
                            <div className="text-danger small">
                              {errors.nationality}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label className="form-label fw-semibold">
                            {t("Full Name")}
                          </label>
                          <Field
                            name="name"
                            className={`form-control ${touched.name && errors.name ? "is-invalid" : ""}`}
                            placeholder={
                              planDetails?.coverPlan === "Individual Medical"
                                ? t("Full Name")
                                : t("Insured Name")
                            }
                          />
                          <ErrorMessage
                            name="name"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label className="form-label fw-semibold">
                            {t("Email ID")}
                          </label>
                          <Field
                            name="email"
                            type="email"
                            className={`form-control ${touched.email && errors.email ? "is-invalid" : ""}`}
                            placeholder={t("Email ID")}
                            disabled={otpVerified}
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label className="form-label fw-semibold">
                            {t("Mobile Number")}
                          </label>
                          <Field
                            name="mobile"
                            className={`form-control ${touched.mobile && errors.mobile ? "is-invalid" : ""}`}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, "");
                              if (val.length <= 8) setFieldValue("mobile", val);
                            }}
                            placeholder={t("Mobile Number")}
                            disabled={otpVerified}
                          />
                          <ErrorMessage
                            name="mobile"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label className="form-label fw-semibold">
                            {t("Occupation")}
                          </label>
                          <Select
                            name="occupation"
                            isClearable
                            options={options}
                            value={selectedOccupation}
                            onChange={(opt) => {
                              if (opt) {
                                setFieldValue("occupation_code", opt.value); // store CODE
                                setFieldValue("occupation", opt.label); // store LABEL
                              } else {
                                setFieldValue("occupation_code", "");
                                setFieldValue("occupation", "");
                              }
                            }}
                            placeholder={t("Select Occupation")}
                          />
                          {touched.occupation && errors.occupation && (
                            <div className="text-danger small">
                              {errors.occupation}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label className="form-label fw-semibold">
                            {t("Residential Location")}
                          </label>
                          <Field
                            name="location"
                            className={`form-control ${touched.location && errors.location ? "is-invalid" : ""}`}
                            placeholder={t("For example(Muscat)")}
                          />
                          <ErrorMessage
                            name="location"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label className="form-label fw-semibold">
                            {t("VATIN (optional)")}
                          </label>
                          <Field
                            name="vatin"
                            className={`form-control ${touched.vatin && errors.vatin ? "is-invalid" : ""}`}
                            onChange={(e) => {
                              const val = e.target.value.replace(
                                /[^a-zA-Z0-9]/g,
                                "",
                              ); // allow letters + numbers
                              if (val.length <= 15) {
                                setFieldValue("vatin", val);
                              }
                            }}
                            placeholder={t("Please enter VATIN number")}
                          />
                          <ErrorMessage
                            name="vatin"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label
                            htmlFor="height"
                            className="form-label fw-semibold"
                          >
                            {t("Height (cm)")}
                          </label>
                          <Field
                            id="height"
                            name="height"
                            placeholder={t("Height (in cms)")}
                            className={`form-control ${touched.height && errors.height ? "is-invalid" : ""}`}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, ""); // numbers only
                              if (val.length <= 3) {
                                setFieldValue("height", val);
                              }
                            }}
                          />
                          <ErrorMessage
                            name="height"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label
                            htmlFor="weight"
                            className="form-label fw-semibold"
                          >
                            {t("Weight (kg)")}
                          </label>
                          <Field
                            id="weight"
                            name="weight"
                            placeholder={t("Weight (in kgs)")}
                            className={`form-control ${touched.weight && errors.weight ? "is-invalid" : ""}`}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, ""); // numbers only
                              if (val.length <= 3) {
                                setFieldValue("weight", val);
                              }
                            }}
                          />
                          <ErrorMessage
                            name="weight"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        {values.bmi >= 38 && (
                          <div className="">
                            <label
                              htmlFor="bmi"
                              className="form-label fw-semibold"
                            >
                              {t("Body Mass Index")}
                            </label>
                            <div
                              className={`fw-bold bmi-height ${Number(values.bmi) >= 38 ? "text-danger" : "text-success"}`}
                            >
                              {values.bmi}
                            </div>
                            {Number(values.bmi) >= 38 && (
                              <div className="text-danger fw-semibold mb-0">
                                {t("You are not eligible for this insurance")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label className="form-label fw-semibold">
                            {t("Passport Number")}
                          </label>
                          <Field
                            name="passportNumber"
                            className={`form-control ${touched.passportNumber && errors.passportNumber ? "is-invalid" : ""}`}
                            placeholder={t("Passport Number")}
                            onChange={(e) => {
                              const val = e.target.value.replace(
                                /[^a-zA-Z0-9]/g,
                                "",
                              );
                              setFieldValue("passportNumber", val);
                            }}
                          />
                          <ErrorMessage
                            name="passportNumber"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label className="form-label fw-semibold">
                            {t("Expiry Date of Passport")}
                          </label>
                          <DatePicker
                            value={getPickerDate(values.passportExpiryDate)}
                            onChange={(value) => {
                              setFieldValue(
                                "passportExpiryDate",
                                value && value.isValid?.()
                                  ? value.format("DD-MM-YYYY")
                                  : "",
                              );
                            }}
                            format="DD-MM-YYYY"
                            disablePast
                            slotProps={{
                              textField: {
                                name: "passportExpiryDate",
                                className: `form-control ${
                                  touched.passportExpiryDate &&
                                  errors.passportExpiryDate
                                    ? "is-invalid"
                                    : ""
                                }`,
                                onBlur: () =>
                                  setFieldTouched("passportExpiryDate", true),
                                onKeyDown: (e) => {
                                  if (e.key !== "Tab") e.preventDefault();
                                },
                                sx: {
                                  "& input": {
                                    height: "38px",
                                    padding: "0.375rem 0.75rem",
                                    boxSizing: "border-box",
                                  },
                                },
                              },
                            }}
                          />

                          <ErrorMessage
                            name="passportExpiryDate"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </div>
                    </div>
                    {/* <div className="row">
                      <div className="col-md-4">
                        <div className="mb-3 minHeightNinety">
                          <label className="form-label fw-semibold">
                            {t("Type of Transaction")}
                          </label>
                          <Field
                            as="select"
                            name="typeOfTransaction"
                            className="form-select"
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              setFieldValue("typeOfTransaction", selectedValue);

                              if (
                                !shouldShowPreviousPolicyFields(selectedValue)
                              ) {
                                setFieldValue("previousPolicyNumber", "");
                                setFieldValue("previousPolicyExpiryDate", "");
                                setFieldValue("previousCompanyName", "");
                              }
                            }}
                          >
                            <option value="">{t("Select Type of Transaction")}</option>
                            {transactionOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Field>
                        </div>
                      </div>
                      {shouldShowPreviousPolicyFields(
                        values.typeOfTransaction,
                      ) && (
                          <>
                            <div className="col-md-4">
                              <div className="mb-3 minHeightNinety">
                                <label className="form-label fw-semibold">
                                  {t("Previous Policy Number")}
                                </label>
                                <Field
                                  name="previousPolicyNumber"
                                  className="form-control"
                                  placeholder={t("Previous Policy Number")}
                                />
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="mb-3 minHeightNinety">
                                <label className="form-label fw-semibold">
                                  {t("Previous Policy Expiry Date")}
                                </label>
                                <DatePicker
                                  value={getPickerDate(
                                    values.previousPolicyExpiryDate,
                                  )}
                                  onChange={(value) => {
                                    setFieldValue(
                                      "previousPolicyExpiryDate",
                                      value && value.isValid?.()
                                        ? value.format("DD-MM-YYYY")
                                        : "",
                                    );
                                  }}
                                  format="DD-MM-YYYY"
                                  disablePast
                                  slotProps={{
                                    textField: {
                                      name: "previousPolicyExpiryDate",
                                      className: "form-control",
                                      onBlur: () =>
                                        setFieldTouched(
                                          "previousPolicyExpiryDate",
                                          true,
                                        ),
                                      onKeyDown: (e) => {
                                        if (e.key !== "Tab") e.preventDefault();
                                      },
                                      sx: {
                                        "& input": {
                                          height: "38px",
                                          padding: "0.375rem 0.75rem",
                                          boxSizing: "border-box",
                                        },
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="mb-3 minHeightNinety">
                                <label className="form-label fw-semibold">
                                  {t("Previous Company Name")}
                                </label>
                                <Field
                                  name="previousCompanyName"
                                  className="form-control"
                                  placeholder={t("Previous Company Name")}
                                />
                              </div>
                            </div>
                          </>
                        )}
                    </div> */}
                  </div>

                   <div className="row mt-3">
                    <div
                      className={`col-12 text-start ${i18n.language === "ar" ? "text-end" : "text-start"}`}
                    >
                      <div className="card shadow mt-3">
                        <div className="card-body">
                          <h5 className="form-label title">
                            {t("Communication Preferences")}
                          </h5>
                          <div className="row mt-3">
                            <p className="communication-header fw-bold">
                              {t("Your Communication Preferences and Consent")}
                            </p>
                            <p className="communication-content">
                              {t(
                                "Please select how you’d like to receive policy documents and communications.",
                              )}
                              {t(
                                "By choosing your preferred mode, you consent to receiving documents and related updates electronically.",
                              )}
                              {t("You can update these preferences anytime.")}
                              {t(
                                "Preferred Communication Mode (I consent for receiving policy documents, related updates and communications electronically)",
                              )}
                            </p>
                          </div>

                          <div className="row">
                            <div className="col-md-4">
                              <div className="mb-3 minHeightNinety">
                                <TextInputSecondary
                                  label={t("Mobile Number (Valid Oman Number Only)")}
                                  name="mobile"
                                  type="text"
                                  value={values.mobile}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /\D/g,
                                      "",
                                    );
                                    if (val.length <= 8)
                                      setFieldValue("mobile", val);
                                    if (sameAsMobile) {
                                      setFieldValue("phone", "+968" + val);
                                    }
                                  }}
                                  placeholder={t("Mobile Number")}
                                  required
                                  requiredMsg="Mobile number is required"
                                  isFieldChecked={mobileChecked}
                                  disabled={otpVerified || !mobileChecked}
                                  handleSecondaryCheck={handleMobilecheck}
                                  flagSrc={omanFlag}
                                  flagAlt="Oman flag"
                                  prefixText="+968"
                                  prefixWidth={82}
                                />
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="mb-3 minHeightNinety">
                                <div className="d-flex mb-2">
                                  <div className="form-check form-check-inline me-3">
                                    <input
                                      className="form-check-input rounded-4"
                                      type="checkbox"
                                      id="whatsappCheck"
                                      checked={whatsappChecked}
                                      onChange={(e) => {
                                        setWhatsappChecked(e.target.checked);
                                        if (!e.target.checked) {
                                          setSameAsMobile(false);
                                          setFieldValue("phone", "+968");
                                        }
                                      }}
                                    />
                                    <label
                                      className="form-check-label fw-semibold"
                                      htmlFor="whatsappCheck"
                                    >
                                      {t("WhatsApp Number")}
                                    </label>
                                  </div>

                                  <div className="form-check form-check-inline">
                                    <input
                                      className="form-check-input rounded-4"
                                      type="checkbox"
                                      id="sameAsMobile"
                                      checked={sameAsMobile}
                                      disabled={!whatsappChecked}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setSameAsMobile(checked);
                                        if (checked) {
                                          setFieldValue("phone", values.mobile);
                                        } else {
                                          setFieldValue("phone", "+968");
                                        }
                                      }}
                                    />
                                    <label
                                      className="form-check-label fw-semibold"
                                      htmlFor="sameAsMobile"
                                    >
                                      {t("Same as Mobile No")}
                                    </label>
                                  </div>
                                </div>
                                <PhoneInput
                                  country="om"
                                  value={
                                    sameAsMobile
                                      ? "+968" + values.mobile
                                      : values.phone
                                  }
                                  onChange={(val) => {
                                    if (sameAsMobile) setSameAsMobile(false);
                                    setFieldValue("phone", val);
                                  }}
                                  inputProps={{ name: "phone" }}
                                  containerClass="w-100 phone-container"
                                  inputClass="form-control w-100 phone-input"
                                  enableAreaCodes={false}
                                  disabled={
                                    otpVerified ||
                                    (!whatsappChecked && !sameAsMobile) ||
                                    sameAsMobile
                                  }
                                />
                                <ErrorMessage
                                  name="phone"
                                  component="div"
                                  className="text-danger small"
                                />
                              </div>
                            </div>

                            <div className="col-md-4">
                              <div className="mb-3 minHeightNinety">
                                <TextInputSecondary
                                  label={t("Email ID")}
                                  name="email"
                                  type="email"
                                  value={values.email}
                                  onChange={(e) =>
                                    setFieldValue("email", e.target.value)
                                  }
                                  placeholder={t("Email ID")}
                                  isFieldChecked={emailChecked}
                                  disabled={otpVerified || !emailChecked}
                                  handleSecondaryCheck={handleemailcheck}
                                />
                              </div>
                            </div>

                            <div className="col-md-12 d-flex justify-content-end align-items-end">
                              {!otpVerified && (
                                <div className="d-flex justify-content-end align-items-end w-100 mt-3">
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      // Validate the entire form before sending OTP
                                      const formErrors = await new Promise(
                                        (resolve) => {
                                          // Use setTimeout to ensure validation completes
                                          setTimeout(async () => {
                                            const errors =
                                              await validationSchema
                                                .validate(values, {
                                                  abortEarly: false,
                                                })
                                                .then(
                                                  () => ({}),
                                                  (err) => {
                                                    const errorMap = {};
                                                    err.inner.forEach((e) => {
                                                      errorMap[e.path] =
                                                        e.message;
                                                    });
                                                    return errorMap;
                                                  },
                                                );
                                            resolve(errors);
                                          }, 0);
                                        },
                                      );

                                      if (Object.keys(formErrors).length > 0) {
                                        // Form has validation errors
                                        setErrors(formErrors);
                                        Object.keys(formErrors).forEach(
                                          (field) =>
                                            setFieldTouched(field, true),
                                        );
                                        setErrMessage(
                                          "Please fill all mandatory fields.",
                                        );
                                        dispatch(
                                          modalOpenMedical({
                                            open: true,
                                            message: "Form Validation Failed",
                                            errors: [
                                              "Please fill all mandatory fields.",
                                            ],
                                          }),
                                        );
                                        return;
                                      }

                                      // Form is valid, proceed with OTP
                                      sendOtp(values);
                                    }}
                                    className="btn btn-primary"
                                  >
                                    {t("Send OTP")}
                                  </button>
                                </div>
                              )}

                              {otpVerified && (
                                <div className="text-success fw-bold d-flex align-items-center mt-3">
                                  <img
                                    src={greenTick}
                                    alt="Success"
                                    className="me-2"
                                    style={{ width: "24px" }}
                                  />
                                  {t("OTP Verified Successfully")}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> 

                  <div className="row mt-3">
                    <div
                      className={
                        i18n.language === "ar"
                          ? "col-6 text-end"
                          : "col-6 text-start"
                      }
                    >
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleBack}
                      >
                        {t("Back")}
                      </button>
                    </div>
                    <div
                      className={
                        i18n.language === "ar"
                          ? "col-6 text-start"
                          : "col-6 text-end"
                      }
                    >
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!otpVerified || Number(values.bmi) >= 38}
                        // disabled={Number(values.bmi) >= 38}
                      >
                        {t("Proceed")}
                      </button>
                    </div>
                  </div>
                </Form>
              </LocalizationProvider>
            </>
          );
        }}
      </Formik>
      <OtpModal
        show={openOtpModal}
        handleClose={() => setOpenOtpModal(false)}
        handleVerified={() => handleVerified()}
        formData={{
          mobileNo: formData.mobile,
          eMailId: formData.email,
          phone: formData.phone,
          ...otpResult,
        }}
        sendOtp={sendOtp}
      />
    </div>
  );
}

export default PersonalInformation;
